import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users, LogOut, Stethoscope, HeartPulse, Search} from "lucide-react";
import { Select, TextInput } from "./components/shared/FormFields";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Layouts from "../../Layouts";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import { useDispatch, useSelector } from "react-redux";
import { FaEye, FaHistory, FaInbox, FaInfoCircle, FaSpinner } from "react-icons/fa";
import { fetchActiveIpdAssignment, fetchIpdList } from "../../../Redux/actions/ipdActions";
import usePatientSearch from "../../../hooks/usePatientSearch";
import { FiInfo } from "react-icons/fi";

const PAGE_SIZE = 5;

const WARDS = ["All Wards", "ICU", "General Ward", "Semi Private", "Private"];
const STATUSES = ["All Status", "Critical", "Stable", "Observation"];

const STAT_CARDS = [
  { key: "totalAssigned", label: "Total Assigned patients", sub: "All Time", icon: Users, color: "bg-teal-50 text-teal-600" },
  { key: "todaysDischarge", label: "Todays Discharge", sub: "Today", icon: LogOut, color: "bg-emerald-50 text-emerald-600" },
  { key: "todaysRounds", label: "Today Rounds", sub: "Today", icon: Stethoscope, color: "bg-orange-50 text-orange-600" },
  { key: "criticalPatients", label: "Critical Patients", sub: "Currently", icon: HeartPulse, color: "bg-red-50 text-red-600" },
];

/**
 * Nurses column cell (Figma image 2): shows primary nurse + "(+N)" and an
 * info icon that opens a popover listing every assigned nurse with their
 * shift window, plus a "View All Nurses" link into the History modal.
 * Assignment data is lazy-loaded once per admission and cached by the parent.
 */
const NursesCell = ({ admissionId, cacheEntry, onRequestLoad, onViewAll }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    if (!cacheEntry || cacheEntry.status === "idle") onRequestLoad(admissionId);
    setOpen((o) => !o);
  };

  const nurses = cacheEntry?.data?.nurseAssignments || [];
  const loading = cacheEntry?.status === "loading";

  if (loading) {
    return <FaSpinner className="animate-spin text-slate-400 text-xs" />;
  }

  if (!nurses.length) {
    return <span className="text-slate-400">—</span>;
  }

  const primary = nurses.find((n) => n.primaryNurse === "Y") || nurses[0];
  const extra = nurses.length - 1;

  return (
    <div className="relative inline-flex items-center gap-1.5" ref={wrapperRef}>
      <span className="font-semibold text-indigo-600 text-sm">
        {primary.nurseName || `Nurse #${primary.nurseId}`}
        {extra > 0 && ` (+${extra})`}
      </span>
      <button
        type="button"
        onClick={handleToggle}
        className="text-slate-400 hover:text-indigo-600"
      >
        <FaInfoCircle className="text-xs" />
      </button>

      {open && (
        <div className="absolute z-20 top-6 left-0 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-left whitespace-normal">
          <p className="text-xs font-semibold text-slate-500 mb-2">
            Assigned Nurses
          </p>
          <ul className="space-y-1.5 max-h-40 overflow-y-auto">
            {nurses.map((n) => (
              <li
                key={n.nurseAssignmentId ?? n.nurseId}
                className="flex items-center justify-between text-xs"
              >
                <span className="font-medium text-slate-700">
                  {n.nurseName || `Nurse #${n.nurseId}`}
                </span>
                <span className="text-slate-400">
                  {n.shiftLabel || ""} {n.fromTime?.split(" ")[1]?.slice(0, 5)}
                  {" - "}
                  {n.toTime?.split(" ")[1]?.slice(0, 5)}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onViewAll();
            }}
            className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
          >
            View All Nurses
          </button>
        </div>
      )}
    </div>
  );
};

const SkeletonRow = ({ cols = 11 }) => (
  <tr className="border-b border-slate-100 animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div className="h-3 bg-slate-200 rounded w-3/4" />
      </td>
    ))}
  </tr>
);

export default function Index() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [ward, setWard] = useState("All Wards");
  const [status, setStatus] = useState("All Status");
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [page, setPage] = useState(1);
  const {
  ipdList,
  isLoading,
  addLoading,
  assignLoading,
  updateLoading,
  error,
  assignmentsByAdmissionId,
  assignmentCreating,
  assignmentCreateError,
  assignmentRosterSaving,
  assignmentRosterError,
} = useSelector((state) => state.ipd);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const loginObj = localStorage.getItem("login_result");
  const loggedUser = loginObj ? JSON.parse(loginObj) : null;
  const assignmentCache = assignmentsByAdmissionId;

  const STATUS_STYLE = {
  ADMITTED: "bg-blue-50 text-blue-600 before:bg-blue-500",
  CRITICAL: "bg-red-50 text-red-600 before:bg-red-500",
  UNDER_TREATMENT: "bg-yellow-50 text-yellow-600 before:bg-yellow-500",
  DISCHARGED: "bg-emerald-50 text-emerald-600 before:bg-emerald-500",
};

const STATUS_LABEL = {
  ADMITTED: "Admitted",
  CRITICAL: "Critical",
  UNDER_TREATMENT: "Under Treatment",
  DISCHARGED: "Discharged",
};

// Statuses for which an active assignment / nurse roster is meaningful.
const ACTIVE_STATUSES = ["ADMITTED", "CRITICAL", "UNDER_TREATMENT"];

const resolveDoctorLabel = (id) => [].find((d) => d.value === Number(id))?.label || `Doctor #${id}`;
const resolveUserLabel = (id) => (Number(id) === 55 ? "Admin" : `User #${id}`);

  const loadActiveAssignment = useCallback(
    (admissionId) => dispatch(fetchActiveIpdAssignment(admissionId)),
    [dispatch]
  );

 // ── Patient search hook ───────────────────────────────────────────────────
  const patientSearch = usePatientSearch(loggedUser);

  // ── Pagination payload ────────────────────────────────────────────────────
  const listPayload = useMemo(
    () => ({ pageNo: currentPage, pageSize: PAGE_SIZE }),
    [currentPage]
  );

  // ── Pagination ────────────────────────────────────────────────────────────
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () => {
    if (ipdList.length === PAGE_SIZE) setCurrentPage((p) => p + 1);
  };


  // ── Data fetches ──────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchIpdList(listPayload));
  }, [dispatch, listPayload]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const filteredList = useMemo(() => {
    if (!searchTerm.trim()) return ipdList;
    const lower = searchTerm.toLowerCase();
    return ipdList.filter(
      (item) =>
        item.admissionNo?.toLowerCase().includes(lower) ||
        item.bedNumber?.toLowerCase().includes(lower) ||
        item.patientName?.toLowerCase().includes(lower) ||
        String(item.patientId ?? "").includes(lower)
    );
  }, [ipdList, searchTerm]);

  // Batch-load active assignments for the current page (Nurses + Last Assigned
  // columns) — one call per row rather than per hover, since PAGE_SIZE is small.
  useEffect(() => {
    filteredList.forEach((item) => {
      if (
        item.admissionId &&
        ACTIVE_STATUSES.includes(item.admissionStatus) &&
        !assignmentCache[item.admissionId]
      ) {
        loadActiveAssignment(item.admissionId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredList]);

  const resetPage = (fn) => (value) => { fn(value); setPage(1); };

    // ── Navigation ────────────────────────────────────────────────────────────
  const handleViewClick = useCallback(
    (admission) => {
      history.push(APP_ROUTES.INPATIENTDETAIL, { ipdData:admission });
    },
    [history]
  );


  const openHistoryFor = async (patient) => {
    setSelectedPatient(patient);
    setOpenHistoryModal(true);
    if (
      !assignmentCache[patient.admissionId] ||
      assignmentCache[patient.admissionId].status === "idle"
    ) {
      await loadActiveAssignment(patient.admissionId);
    }
  };

  return (
    <Layouts>
      <div className="min-h-full px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1400px] space-y-6">
          <h1 className="text-2xl font-semibold text-slate-900">In Patients</h1>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STAT_CARDS.map(({ key, label, sub, icon: Icon, color }) => (
              <div key={key} className="h-28 rounded-xl border border-brand-colorBorder bg-white p-3 shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex gap-2.5 items-center justify-center">
                    <div className={`h-11 w-11 flex items-center justify-center rounded-lg ${color}`}>
                      <Icon size={20} />
                     </div>
                    <p className="text-sm font-medium text-slate-800">{label}</p>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-2xl font-semibold text-slate-800">0</p>
                    <p className="text-xs text-slate-400">{sub}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <TextInput
                  placeholder="Search by Patient Name, UHID, Bed No"
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <Select value={ward} onChange={(e) => resetPage(setWard)(e.target.value)} className="lg:w-48">
                {WARDS.map((w) => <option key={w} value={w}>{w}</option>)}
              </Select>

              <Select value={status} onChange={(e) => resetPage(setStatus)(e.target.value)} className="lg:w-48">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>

              <label className="flex items-center gap-2 whitespace-nowrap text-sm text-slate-600">
                Critical Only
                <button
                  role="switch"
                  aria-checked={criticalOnly}
                  onClick={() => resetPage(setCriticalOnly)(!criticalOnly)}
                  className={`relative h-6 w-11 rounded-full transition ${criticalOnly ? "bg-teal-600" : "bg-slate-200"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${criticalOnly ? "left-5" : "left-0.5"}`} />
                </button>
              </label>
            </div>
          </div>
            <>
              {/* Table */}
        <div className="overflow-auto rounded-lg border border-slate-200">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#F1FFF9] text-slate-700 border-b text-sm whitespace-nowrap">
                <th className="px-5 py-3 font-semibold">Patient</th>
                <th className="px-5 py-3 font-semibold">UHID</th>
                <th className="px-5 py-3 font-semibold">Age / Gender</th>
                <th className="px-5 py-3 font-semibold">Ward</th>
                <th className="px-5 py-3 font-semibold">Bed</th>
                <th className="px-5 py-3 font-semibold">Diagnosis</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Admitted On</th>
                <th className="px-5 py-3 font-semibold">Nurses</th>
                <th className="px-5 py-3 font-semibold">Last Assigned</th>
                
                <th className="px-5 py-3 font-semibold text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonRow key={i} cols={11} />
                ))}

              {!isLoading && filteredList.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-16 text-center text-slate-400">
                    <FaInbox className="text-4xl mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">
                      {searchTerm
                        ? "No admissions match your search."
                        : "No IPD admissions found."}
                    </p>
                  </td>
                </tr>
              )}

              {!isLoading &&
                filteredList.map((item, index) => {
                  const statusKey = item.admissionStatus || "";
                  const statusClass =
                    STATUS_STYLE[statusKey] ||
                    "bg-slate-50 text-slate-600 before:bg-slate-400";
                  const doctorId = item.assignedDoctorId || item.doctorId;
                  const cacheEntry = assignmentCache[item.admissionId];
                  const lastAssignedData = cacheEntry?.data;

                  return (
                    <tr
                      key={item.admissionId ?? index}
                      className="border-b border-slate-100 text-sm text-slate-700 hover:bg-slate-50 transition-colors whitespace-nowrap"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-semibold text-slate-800">
                              {item.patientName || "—"}
                            </p>
                            <p className="text-xs text-slate-400">
                              {item.patientId || ""}
                            </p>
                          </div>
                        </div>
                      </td>
                       <td className="px-5 py-3 font-bold text-indigo-600">
                        {item.uhid || "—"}
                      </td>
                      <td className="px-5 py-3">
                        {item.age ?? "—"} / {item.gender ?? "—"}
                      </td>
                      <td className="px-5 py-3">{item.wardCode || "—"}</td>
                      <td className="px-5 py-3 font-semibold">
                        {item.bedNumber || "—"}
                      </td>
                      <td className="px-5 py-3">{item.diagnosis || "—"}</td>
                      <td className="px-5 py-3">
                        <span
                          className={`relative inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold
                            before:content-[''] before:w-2 before:h-2 before:rounded-full ${statusClass}`}
                        >
                          {STATUS_LABEL[statusKey] || statusKey || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3">{item.admissionDate}</td>
                      <td className="px-5 py-3">
                        {ACTIVE_STATUSES.includes(statusKey) ? (
                          <NursesCell
                            admissionId={item.admissionId}
                            cacheEntry={cacheEntry}
                            onRequestLoad={loadActiveAssignment}
                            onViewAll={() => openHistoryFor(item)}
                          />
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {lastAssignedData ? (
                          <div>
                            <p className="font-medium text-slate-700">
                              {(
                                lastAssignedData.modifiedDate ||
                                lastAssignedData.createdDate ||
                                ""
                              ).split(" ")[0] || "—"}
                            </p>
                            <p className="text-xs text-slate-400">
                              by{" "}
                              {resolveUserLabel(
                                lastAssignedData.modifiedBy ||
                                  lastAssignedData.createdBy
                              )}
                            </p>
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      
                      <td className="px-5 py-3">
                        <div className="flex justify-center items-center gap-2">
                          <button
                            onClick={() => handleViewClick(item)}
                            className="px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600 text-xs font-bold flex items-center gap-1.5 hover:bg-indigo-100 transition-colors"
                          >
                            <FaEye className="text-xs" />
                            View
                          </button>
                          <button
                            onClick={() => openHistoryFor(item)}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-xs font-bold flex items-center gap-1.5 hover:bg-slate-100 transition-colors"
                            title="Assignment History"
                          >
                            <FaHistory className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-slate-500">
            Showing {filteredList.length} IPD admission
            {filteredList.length !== 1 ? "s" : ""}
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
          <div className="flex items-center gap-2">
            <span
              onClick={handlePrevPage}
              disabled={currentPage === 1 || isLoading}
              className="cursor-pointer px-3 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Prev
            </span>
            <span className="text-xs text-slate-600 font-medium px-1">
              Page {currentPage}
            </span>
            <span
              onClick={handleNextPage}
              disabled={ipdList.length < PAGE_SIZE || isLoading}
              className="cursor-pointer px-3 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Next
            </span>
          </div>
        </div>
            </>
        </div>
      </div>

      {openHistoryModal && selectedPatient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">

            <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b bg-[#F1FFF9]">
              <h2 className="text-slate-800 font-bold text-base">
                Assignment History —{" "}
                {selectedPatient.patientName || selectedPatient.patientId || "Patient"} (
                {selectedPatient.admissionNo ?? ""})
              </h2>
              <span
                onClick={() => setOpenHistoryModal(false)}
                className="cursor-pointer text-gray-500 hover:text-red-500 text-2xl font-bold leading-none"
              >
                ×
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {assignmentCache[selectedPatient.admissionId]?.status === "loading" ? (
                <div className="py-10 flex items-center justify-center text-slate-400 text-sm gap-2">
                  <FaSpinner className="animate-spin" />
                  Loading history…
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-slate-600">
                        <th className="pb-3 font-semibold">Date</th>
                        <th className="pb-3 font-semibold">Primary Doctor</th>
                        <th className="pb-3 font-semibold">Duty Doctor</th>
                        <th className="pb-3 font-semibold">Nurse</th>
                        <th className="pb-3 font-semibold">Shift</th>
                        <th className="pb-3 font-semibold">From</th>
                        <th className="pb-3 font-semibold">To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const data = assignmentCache[selectedPatient.admissionId]?.data;
                        if (!data || !data.nurseAssignments?.length) {
                          return (
                            <tr>
                              <td colSpan={7} className="py-10 text-center text-slate-400">
                                No assignment history available for this patient.
                              </td>
                            </tr>
                          );
                        }
                        const primaryDoctorLabel = data.primaryDoctorId
                          ? resolveDoctorLabel(data.primaryDoctorId)
                          : "—";
                        // Group nurse rows by assignDate to mirror the Figma layout.
                        const byDate = data.nurseAssignments.reduce((acc, n) => {
                          (acc[n.assignDate] = acc[n.assignDate] || []).push(n);
                          return acc;
                        }, {});
                        return Object.entries(byDate).map(([date, nurses]) => {
                          const dutyDoctor = data.dutyDoctors?.find(
                            (d) => d.assignDate === date
                          );
                          return (
                            <tr key={date} className="border-b border-slate-100">
                              <td className="py-4 font-semibold align-top">{date}</td>
                              <td className="py-4 font-semibold align-top">
                                {primaryDoctorLabel}
                              </td>
                              <td className="py-4 font-semibold align-top">
                                {dutyDoctor ? dutyDoctor.doctorName : "—"}
                              </td>
                              <td className="py-4 align-top">
                                <div className="space-y-2">
                                  {nurses.map((n) => (
                                    <p key={n.nurseAssignmentId} className="font-semibold">
                                      {n.nurseName}
                                    </p>
                                  ))}
                                </div>
                              </td>
                              <td className="py-4 align-top">
                                <div className="space-y-2">
                                  {nurses.map((n) => (
                                    <p key={n.nurseAssignmentId} className="font-semibold">
                                      {n.shiftLabel}
                                    </p>
                                  ))}
                                </div>
                              </td>
                              <td className="py-4 align-top">
                                <div className="space-y-2">
                                  {nurses.map((n) => (
                                    <p key={n.nurseAssignmentId} className="font-semibold">
                                      {n.fromTime?.split(" ")[1]?.slice(0, 5)}
                                    </p>
                                  ))}
                                </div>
                              </td>
                              <td className="py-4 align-top">
                                <div className="space-y-2">
                                  {nurses.map((n) => (
                                    <p key={n.nurseAssignmentId} className="font-semibold">
                                      {n.toTime?.split(" ")[1]?.slice(0, 5)}
                                    </p>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4 flex items-center gap-3">
                <FiInfo size={18} className="text-indigo-600 shrink-0" />
                <p className="text-sm font-medium text-indigo-600">
                  History currently reflects the active assignment. A
                  dedicated history-list endpoint is needed to show fully
                  completed/past assignments.
                </p>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setOpenHistoryModal(false)}
                  className="px-4 py-1.5 border border-indigo-300 rounded-md text-sm font-medium text-white transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layouts>
  );
}
